import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Accordion, AccordionSummary, AccordionDetails, Button, Typography, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { loadWarehouses, saveWarehouse } from '../modules/warehousedata';
import FooterPadding from '../main/footerpadding';
import Footer from '../main/footer';
import AddIcon from '@mui/icons-material/Add';
import AddWarehouseDialog from './addwarehousedialog';
import vadoTheme from '../main/VadoThemeMui5'; // Import VadoTheme for mui 5
import WarehouseView from './warehouseview';
import ObjectIdGenerator from '../modules/objectidgenerator';
import { SPINNER_ON, SPINNER_OFF } from '../modules/appstate';
import Warehouse from '../domain/warehouse';

export default function Warehouses(props) {
    const warehouses = useSelector((state) => state.warehousedata.warehouses);
    const loadingData = useSelector((state) => state.warehousedata.fetchingData);
    const customers = useSelector((state) => state.customerdata.allCustomers);
    const warehousesById = useSelector((state) => state.warehousedata.warehousesById);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [addNewWarehouseOpen, setAddNewWarehouseOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // allow multiple expanded panels: map of panelId -> boolean
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        if (!warehouses && !loadingData) {
            dispatch(loadWarehouses());
        }
    }, [warehouses, loadingData, dispatch]);

    const onNewWarehouse = () => {
        setAddNewWarehouseOpen(true);
    };

    const onNewWarehouseAdded = async (warehouseData) => {
        dispatch({ type: SPINNER_ON });
        setSaving(true);
        setAddNewWarehouseOpen(false);
        const id = await ObjectIdGenerator.newId();

        const newWarehouse = new Warehouse({
            id,
            name: warehouseData.name,
            isVirtual: false,
            allowOrderFrom: warehouseData.allowOrderFrom,
            allowOrderTo: warehouseData.allowOrderTo,
            users: [],
            customer: warehouseData.customer,
        });
        if (!(await saveWarehouse(newWarehouse)(dispatch))) {
        } else {
        }
        dispatch({ type: SPINNER_OFF });
        setSaving(false);
    };

    const cancelAddWarehouse = () => {
        setAddNewWarehouseOpen(false);
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
    };

    // order warehouses by name (fallback to id)
    const ordered = warehouses ? orderBy(warehouses, [(w) => w.name.toString().toLowerCase()], ['asc']) : [];

    return (
        <ThemeProvider theme={vadoTheme}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {ordered.map((w) => (
                        <Accordion key={w.id} expanded={!!expanded[w.id]} onChange={handleAccordionChange(w.id)}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`warehouse-${w.id}-content`}
                                id={`warehouse-${w.id}-header`}>
                                <Typography variant="subtitle1">{w.name}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <WarehouseView id={w.id} warehouse={warehousesById[w.id]} expanded={expanded[w.id]} />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    {!ordered.length && (
                        <Grid item xs={12}>
                            <Typography variant="body1">{t('warehouse.noWarehouses')}</Typography>
                        </Grid>
                    )}
                    <Footer>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                disabled={saving}
                                onClick={onNewWarehouse}>
                                {t('buttons.add')}
                            </Button>
                        </Grid>
                    </Footer>
                    <FooterPadding />
                    <AddWarehouseDialog
                        open={addNewWarehouseOpen}
                        customers={customers}
                        addCb={onNewWarehouseAdded}
                        cancelCb={cancelAddWarehouse}
                    />
                </Grid>
            </Grid>
        </ThemeProvider>
    );
}
